import React, { useState, useMemo, useEffect } from 'react';
import { Container, Button, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { analyzePortfolio } from '../api';
import { toast } from 'react-toastify';

const PortfolioDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [mesesDisponibles, setMesesDisponibles] = useState([]);
  const [selectedMonthId, setSelectedMonthId] = useState('ALL');
  const [dynamicTitle, setDynamicTitle] = useState('REPORTE GLOBAL');

  // Asegurar scroll arriba al cambiar datos
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedMonthId, rawData]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    setError(null);
    try {
      const result = await analyzePortfolio(file);
      // Blindaje: Asegurar que registros sea siempre un array
      setRawData(result.registros || []);
      setMesesDisponibles(result.meses || []);
      setSelectedMonthId('ALL');
      setDynamicTitle('REPORTE GLOBAL');
      toast.success("Excel cargado correctamente");
    } catch (error) {
      console.error(error);
      setError("Error al procesar el Excel. Verifica que sea el archivo 'ProcesarVoucher' correcto.");
      toast.error("Error al procesar el archivo");
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (val) => "$" + Math.round(val || 0).toLocaleString('es-CL');

  // Lógica de filtrado y cálculo de KPIs
  const dashboardData = useMemo(() => {
    if (!rawData || rawData.length === 0) return null;

    const filtered = selectedMonthId === 'ALL' 
      ? rawData 
      : rawData.filter(d => d.Mes_ID === selectedMonthId);

    if (filtered.length === 0) return null;

    const partData = filtered.filter(d => d['Forma de pago'] === 'particular');
    const totalPart = partData.reduce((acc, d) => acc + (d['$ Total'] || 0), 0);

    const convData = filtered.filter(d => d['Forma de pago'] === 'convenio');
    const totalConv = convData.reduce((acc, d) => acc + (d['$ Total'] || 0), 0);

    const cats = ['No Prefacturado', 'Prefacturado', 'Facturado'];
    const stats = {};
    cats.forEach(cat => {
      const d = convData.filter(x => x.Categoria === cat);
      stats[cat] = {
        monto: d.reduce((acc, x) => acc + (x['$ Total'] || 0), 0),
        cant: d.length,
        pct: totalConv > 0 ? (d.reduce((acc, x) => acc + (x['$ Total'] || 0), 0) / totalConv * 100).toFixed(2) : "0.00",
        clients: d.reduce((acc, x) => {
          const clientName = x['Nombre cliente'] || 'S/I';
          acc[clientName] = (acc[clientName] || { m: 0, c: 0 });
          acc[clientName].m += (x['$ Total'] || 0);
          acc[clientName].c += 1;
          return acc;
        }, {})
      };
    });

    return { totalPart, partCant: partData.length, totalConv, convCant: convData.length, stats };
  }, [rawData, selectedMonthId]);

  const handleMonthChange = (e) => {
    const val = e.target.value;
    setSelectedMonthId(val);
    if (val === 'ALL') {
      setDynamicTitle('REPORTE GLOBAL');
    } else {
      const mes = mesesDisponibles.find(m => m.Mes_ID === val);
      setDynamicTitle(mes ? mes.Mes_Display : val);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <Spinner animation="border" variant="indigo" />
        <p className="mt-4 font-bold text-slate-500 uppercase tracking-widest text-xs">Analizando Cartera...</p>
      </div>
    );
  }

  return (
    <Container fluid className="p-4 md:p-8 bg-white min-h-screen text-slate-800" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="max-w-[1440px] mx-auto">
        
        {/* Header con botón de volver corregido */}
        <header className="mb-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="outline-secondary" size="sm" className="fw-bold px-3 rounded-pill border-slate-200">
                ← Volver al Dashboard Principal
              </Button>
            </Link>
            <div>
              <p className="text-[9px] font-black uppercase text-indigo-600 tracking-[0.4em] mb-0.5">Transportes Emilia</p>
              <h1 className="text-sm font-black text-slate-400 tracking-widest uppercase">Control de Cartera</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-slate-50 p-2 rounded-xl border border-slate-200">
              <span className="text-[8px] font-black uppercase text-slate-400 tracking-widest ml-1">Filtro:</span>
              <select 
                className="bg-transparent border-0 font-bold text-xs outline-none cursor-pointer text-slate-700"
                value={selectedMonthId}
                onChange={handleMonthChange}
              >
                <option value="ALL">TOTAL ACUMULADO</option>
                {mesesDisponibles.map(m => (
                  <option key={m.Mes_ID} value={m.Mes_ID}>{m.Mes_Display}</option>
                ))}
              </select>
            </div>
            
            <div className="relative overflow-hidden bg-slate-900 text-white px-4 py-2 rounded-xl text-xs font-bold hover:bg-slate-800 transition-all cursor-pointer border-b-2 border-slate-700 active:border-0 active:translate-y-0.5">
              <span>Cargar Excel</span>
              <input 
                type="file" 
                accept=".xlsx, .xls" 
                onChange={handleFileUpload} 
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
            </div>
          </div>
        </header>

        {error && (
          <Alert variant="danger" className="rounded-2xl border-0 shadow-sm mb-8 font-bold text-sm">
             ⚠️ {error}
          </Alert>
        )}

        {!dashboardData ? (
          <div className="py-20 text-center border-2 border-dashed border-slate-100 rounded-[2rem] bg-slate-50/30">
            <div className="text-4xl mb-4">📊</div>
            <h3 className="font-black text-slate-400 uppercase tracking-tighter italic text-xl">Sin datos procesados</h3>
            <p className="text-slate-400 text-xs font-medium max-w-xs mx-auto mt-2 uppercase tracking-widest">Sube el archivo ProcesarVoucher para visualizar el estado de tu cartera</p>
          </div>
        ) : (
          <>
            {/* KPIs Principales */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-10 border-y border-slate-100 py-6 bg-slate-50/20 rounded-2xl px-6">
              <div className="bg-white border border-sky-100 p-4 rounded-2xl min-w-[220px] shadow-sm flex-1 max-w-xs text-center">
                <p className="text-[9px] text-sky-500 uppercase font-black tracking-widest mb-1">Particulares</p>
                <p className="text-2xl font-black text-slate-900">{formatMoney(dashboardData.totalPart)}</p>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tight">{dashboardData.partCant} SERVICIOS</p>
              </div>

              <div className="flex-1 text-center">
                <h2 className="text-4xl font-black text-slate-900 tracking-tighter uppercase italic leading-none">
                  {dynamicTitle}
                </h2>
                <div className="w-10 h-1 bg-indigo-500 mx-auto mt-3 rounded-full"></div>
              </div>

              <div className="bg-white border border-indigo-100 p-4 rounded-2xl min-w-[220px] shadow-sm flex-1 max-w-xs text-center">
                <p className="text-[9px] text-indigo-500 uppercase font-black tracking-widest mb-1">Convenios</p>
                <p className="text-2xl font-black text-slate-900">{formatMoney(dashboardData.totalConv)}</p>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tight">{dashboardData.convCant} SERVICIOS</p>
              </div>
            </div>

            {/* Grid 3 Columnas */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
              
              {/* Columna 1: No Prefacturado */}
              <div className="space-y-6">
                <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm border-t-8 border-red-500 p-5">
                  <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-1 italic">No Prefacturado</h3>
                  <p className="text-4xl font-black text-red-600 tracking-tighter leading-none mb-4">{formatMoney(dashboardData.stats['No Prefacturado'].monto)}</p>
                  <div className="flex items-center justify-between border-t border-slate-50 pt-3">
                    <span className="px-2 py-1 rounded bg-red-50 text-red-600 font-black text-[10px]">{dashboardData.stats['No Prefacturado'].pct}%</span>
                    <span className="text-[10px] text-slate-400 font-black tracking-widest">{dashboardData.stats['No Prefacturado'].cant} SERV.</span>
                  </div>
                </div>
                
                <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm max-h-[600px] overflow-y-auto">
                  <table className="w-full text-left border-collapse">
                    <tbody className="divide-y divide-slate-50">
                      {renderTableBody(dashboardData.stats['No Prefacturado'], formatMoney)}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Columna 2: Prefacturado */}
              <div className="space-y-6">
                <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm border-t-8 border-amber-500 p-5">
                  <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-1 italic">Prefacturado</h3>
                  <p className="text-4xl font-black text-amber-600 tracking-tighter leading-none mb-4">{formatMoney(dashboardData.stats['Prefacturado'].monto)}</p>
                  <div className="flex items-center justify-between border-t border-slate-50 pt-3">
                    <span className="px-2 py-1 rounded bg-amber-50 text-amber-600 font-black text-[10px]">{dashboardData.stats['Prefacturado'].pct}%</span>
                    <span className="text-[10px] text-slate-400 font-black tracking-widest">{dashboardData.stats['Prefacturado'].cant} SERV.</span>
                  </div>
                </div>

                <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm max-h-[600px] overflow-y-auto">
                  <table className="w-full text-left border-collapse">
                    <tbody className="divide-y divide-slate-50">
                      {renderTableBody(dashboardData.stats['Prefacturado'], formatMoney)}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Columna 3: Facturado */}
              <div className="space-y-6">
                <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm border-t-8 border-emerald-500 p-5">
                  <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-1 italic">Facturado</h3>
                  <p className="text-4xl font-black text-emerald-600 tracking-tighter leading-none mb-4">{formatMoney(dashboardData.stats['Facturado'].monto)}</p>
                  <div className="flex items-center justify-between border-t border-slate-50 pt-3">
                    <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-600 font-black text-[10px]">{dashboardData.stats['Facturado'].pct}%</span>
                    <span className="text-[10px] text-slate-400 font-black tracking-widest">{dashboardData.stats['Facturado'].cant} SERV.</span>
                  </div>
                </div>
                
                <div className="p-6 rounded-2xl border-2 border-dashed border-slate-100 bg-slate-50/10 text-center opacity-60 grayscale">
                  <p className="text-[9px] text-slate-300 font-black uppercase tracking-[0.4em] italic mb-1">
                    Conciliado 2026
                  </p>
                  <p className="text-[8px] text-slate-300 font-bold uppercase tracking-widest">Transportes Emilia</p>
                </div>
              </div>
            </div>
          </>
        )}

        <footer className="mt-20 text-center border-t border-slate-100 pt-10 pb-4">
          <p className="text-slate-200 text-[10px] uppercase font-black tracking-[0.8em]">Transportes Emilia | CONTROL INTERNO</p>
        </footer>
      </div>
    </Container>
  );
};

function renderTableBody(categoryData, formatMoney) {
  if (!categoryData || !categoryData.clients) return null;
  
  const clients = categoryData.clients;
  const categoryTotal = categoryData.monto;
  const sortedClients = Object.entries(clients).sort((a, b) => b[1].m - a[1].m);

  if (sortedClients.length === 0) {
    return (
      <tr>
        <td className="px-6 py-12 text-center text-slate-200 font-black uppercase tracking-[0.3em] text-[10px] italic">Sin movimientos</td>
      </tr>
    );
  }

  return sortedClients.map(([name, data]) => {
    const pct = categoryTotal > 0 ? (data.m / categoryTotal * 100).toFixed(2) : "0.00";
    return (
      <tr key={name} className="hover:bg-slate-50/50 transition-colors group">
        <td className="px-5 py-4 uppercase italic">
          <div className="font-bold text-slate-600 text-sm tracking-tighter leading-none italic group-hover:text-indigo-600 truncate max-w-[200px]">{name}</div>
          <div className="text-[8px] text-slate-400 font-black tracking-widest mt-1.5 uppercase italic">{data.c} SERVICIOS</div>
        </td>
        <td className="px-5 py-4 text-right">
          <div className="font-black text-slate-800 text-lg tracking-tighter leading-none italic">{formatMoney(data.m)}</div>
          <div className="text-[9px] text-indigo-500 font-black tracking-widest mt-1.5 uppercase italic">{pct}% DEL ITEM</div>
        </td>
      </tr>
    );
  });
}

export default PortfolioDashboard;
